import React from 'react';
import { Flame, Beef, Clock, ChefHat, Droplets, ThermometerSun, Sparkles, CheckCircle2 } from 'lucide-react';

const STEPS = [
  {
    n: '01',
    icon: Beef,
    title: 'Escolha a costela certa',
    body:
      'Costela de ripa (também chamada janela) é a campeã — escolhe o tamanho conforme tua rotativa. Procure peças com gordura branca e bem distribuída — é ela que vai derreter, hidratar a carne e dar aquele aroma de tirar o chapéu.',
    tip: 'Compra direto do açougue de confiança. Nada de carne congelada do mercado, tchê!',
  },
  {
    n: '02',
    icon: Sparkles,
    title: 'Tempera só com sal grosso',
    body:
      'Costelão raiz não leva tempero industrializado. Sal grosso, ponto. Salgue uns 30 minutos antes na parte da carne; no lado do osso, mais generoso. A carne pede sal, ela mesma diz quando é o suficiente.',
    tip: 'Sal fino só pra finalizar, se quiser. Marinada e tempero pronto: nem pensar.',
  },
  {
    n: '03',
    icon: Flame,
    title: 'Faça brasa de verdade',
    body:
      'Use carvão de qualidade (eucalipto ou braúna) e prepare uma cama de brasa farta antes de pôr a costela. A rotativa Gaúcha gira com a carne longe das chamas — é o calor da brasa que cozinha, nunca o fogo direto.',
    tip: 'Brasa boa é aquela que fica branca por fora, vermelha por dentro. Espera ela acender direito.',
  },
  {
    n: '04',
    icon: ThermometerSun,
    title: 'Calor baixo e constante',
    body:
      'O segredo do costelão pra lá de bagual é tempo, não pressa. Mantenha brasa entre 110 °C e 130 °C — quente o suficiente pra dourar, baixo o suficiente pra não secar. Vá repondo carvão de leve, sempre nas laterais.',
    tip: 'Se a carne começar a pingar gordura demais e fazer labareda, afasta a brasa. Labareda queima, brasa cozinha.',
  },
  {
    n: '05',
    icon: Clock,
    title: 'Deixa rodar — sem pressa',
    body:
      'O segredo do costelão pra lá de bagual é tempo, não pressa. Dependendo do tamanho da peça e da tua rotativa, são várias horas girando devagar. Não fica espetando o tempo todo. Confia no giro: a rotativa Gaúcha distribui o calor por igual, não tem como queimar de um lado só.',
    tip: 'Começa pelo lado do osso pra baixo. Depois deixa girando. A própria rotativa cuida do resto.',
  },
  {
    n: '06',
    icon: Droplets,
    title: 'Hidrata com cervejada',
    body:
      'A cada 1h ou 2h, dá uma pincelada de cerveja preta misturada com um pouquinho de água — entra pela carne, perfuma, e ainda dá uma casquinha caramelada. Tradição gaúcha, tchê.',
    tip: 'Cerveja preta, vinagrete, ou só água com sal grosso dissolvido. Cada mestre tem sua receita.',
  },
  {
    n: '07',
    icon: ChefHat,
    title: 'Ponto certo: descola do osso',
    body:
      'A costela está pronta quando a carne começa a descolar do osso por gravidade — você dá uma puxadinha leve e ela cede. Se ainda resistir, é mais uma hora. Pressa estraga costelão.',
    tip: 'A casquinha precisa estar dourada, dura por fora, e a carne soltinha por dentro. Aí sim.',
  },
  {
    n: '08',
    icon: CheckCircle2,
    title: 'Descansa, fatia e serve',
    body:
      'Tira da rotativa e deixa descansar 10 minutos coberta com papel alumínio. Aí fatia em pedaços generosos, junto com farinha de mandioca, vinagrete, pão e a galera em volta. Tchê, isso é costelão.',
    tip: 'Costelão de verdade come de pé, com a mão, perto do fogo. Aproveita.',
  },
];

const CostelaoGuideSection = () => {
  return (
    <section
      id="costelao-guide"
      className="relative py-20 bg-gradient-to-b from-stone-900 via-stone-950 to-stone-900 overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, #f59e0b 1px, transparent 1px), radial-gradient(circle at 80% 70%, #ea580c 1px, transparent 1px)',
            backgroundSize: '60px 60px, 80px 80px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4">
            <Flame className="w-4 h-4 text-amber-500" />
            <span className="text-amber-400 text-xs font-bold tracking-widest uppercase">
              Guia do Mestre
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
            Como fazer seu costelão{' '}
            <span className="text-amber-500 italic">pra lá de bagual</span>
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto text-base sm:text-lg">
            8 passos pra transformar uma costela em momento gaúcho de verdade.
            Tradição, paciência e brasa farta — o resto a Rotativa Gaúcha cuida.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.n}
                data-testid={`costelao-step-${i}`}
                className="group relative bg-stone-800/60 border border-stone-700 hover:border-amber-500/50 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10"
              >
                {/* Step number watermark */}
                <span
                  aria-hidden="true"
                  className="absolute top-3 right-4 text-6xl font-black text-amber-500/10 group-hover:text-amber-500/20 transition-colors leading-none select-none"
                >
                  {step.n}
                </span>

                {/* Icon */}
                <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4 shadow-lg shadow-amber-600/30">
                  <Icon className="w-6 h-6 text-stone-900" strokeWidth={2.4} />
                </div>

                {/* Title */}
                <h3 className="relative text-white font-bold text-lg mb-2 leading-tight">
                  {step.title}
                </h3>

                {/* Body */}
                <p className="relative text-stone-400 text-sm leading-relaxed mb-3">
                  {step.body}
                </p>

                {/* Tip */}
                <div className="relative pt-3 border-t border-stone-700/60">
                  <p className="text-amber-400/90 text-xs italic leading-relaxed">
                    💡 {step.tip}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-14 text-center">
          <div className="inline-block bg-gradient-to-br from-amber-500/10 to-orange-600/5 border border-amber-500/30 rounded-2xl px-6 sm:px-10 py-8 max-w-2xl">
            <h3 className="text-white text-xl sm:text-2xl font-bold mb-2">
              Tá pronto pra fazer o costelão da tua vida?
            </h3>
            <p className="text-stone-400 text-sm sm:text-base mb-5">
              Com uma Rotativa Gaúcha, esses 8 passos viram tradição na sua casa.
              Bora conversar?
            </p>
            <button
              data-testid="costelao-guide-cta-btn"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('open-chatbot', {
                    detail: { context: 'Guia do Costelão' },
                  })
                )
              }
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold px-6 py-3 rounded-xl transition-all hover:scale-105 hover:shadow-lg hover:shadow-amber-500/40"
            >
              <Flame className="w-5 h-5" />
              Quero minha Rotativa Gaúcha
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostelaoGuideSection;
